import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { InboxCounts, StaffConversation } from '../../../common/models/support.model';
import { CATEGORY_META, CONVERSATION_STATUS_META } from '../support-meta';
import { AssigneeFilter, StaffAvatar } from './StaffPeople';
import { PriorityBars } from '../components/TaskBadge';

dayjs.extend(relativeTime);

export const INBOX_FILTERS = ['unread', 'open', 'waiting', 'resolved', 'all'] as const;
export type InboxFilter = typeof INBOX_FILTERS[number];

const PRIORITY_BARS = { low: 1, normal: 2, high: 3, urgent: 4 } as const;

/** Left pane: filters with counts, search, and the conversations. */
export function InboxList({
  filter,
  onFilter,
  counts,
  search,
  onSearch,
  assignee,
  onAssignee,
  me,
  items,
  loading,
  selectedId,
  onSelect,
}: {
  filter: InboxFilter;
  onFilter: (f: InboxFilter) => void;
  counts: InboxCounts | null;
  search: string;
  onSearch: (q: string) => void;
  /** '' = everyone, 'none' = nobody, else a teammate's email. */
  assignee: string;
  onAssignee: (a: string) => void;
  me?: string;
  items: StaffConversation[];
  loading: boolean;
  selectedId?: string;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-0 flex-col">
      <div className="flex flex-col gap-2 border-b p-3">
        <div className="flex gap-1 overflow-x-auto rounded-xl bg-muted p-1" role="tablist">
          {INBOX_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              role="tab"
              aria-selected={filter === f}
              onClick={() => onFilter(f)}
              className={cn(
                'flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs transition-colors',
                filter === f ? 'bg-background font-medium shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t(`SUPPORT.STAFF.FILTER.${f.toUpperCase()}`)}
              {counts && counts[f] > 0 && <span className="tabular-nums text-muted-foreground">{counts[f]}</span>}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={t('SUPPORT.STAFF.SEARCH')}
            className="h-8 ps-8 text-sm"
            aria-label={t('SUPPORT.STAFF.SEARCH')}
          />
        </div>
        <AssigneeFilter value={assignee} onChange={onAssignee} me={me} className="h-8 w-32 shrink-0" />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-1.5" data-testid="inbox-list">
        {loading && !items.length ? (
          <div className="flex flex-col gap-2 p-2">
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16" />)}
          </div>
        ) : items.length ? (
          items.map((c) => {
            const status = CONVERSATION_STATUS_META[c.status];
            const CategoryIcon = CATEGORY_META[c.category]?.icon;
            const unread = c.supportUnread > 0;
            return (
              <button
                key={c._id}
                type="button"
                onClick={() => onSelect(c._id)}
                className={cn(
                  'flex w-full flex-col gap-1 rounded-xl px-3 py-2.5 text-start transition-colors hover:bg-muted/70',
                  selectedId === c._id && 'bg-muted',
                )}
                data-testid="inbox-item"
              >
                <span className="flex w-full items-center gap-2">
                  <span className={cn('size-1.5 shrink-0 rounded-full', unread ? 'bg-foreground' : 'bg-transparent')} aria-label={unread ? t('SUPPORT.STAFF.UNREAD') : undefined} />
                  <span className={cn('min-w-0 flex-1 truncate text-sm', unread && 'font-medium')}>{c.vendorName || '—'}</span>
                  <span className="shrink-0 text-[11px] text-muted-foreground">{dayjs(c.lastMessageAt).fromNow(true)}</span>
                </span>
                <span className="flex w-full items-center gap-2 ps-3.5">
                  {CategoryIcon && <CategoryIcon className="size-3 shrink-0 text-muted-foreground" />}
                  <span className={cn('min-w-0 flex-1 truncate text-xs', unread ? 'text-foreground' : 'text-muted-foreground')}>{c.subject}</span>
                </span>
                <span className="flex w-full items-center gap-2 ps-3.5">
                  <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{c.lastMessagePreview}</span>
                  <PriorityBars bars={PRIORITY_BARS[c.priority] ?? 2} tone={c.priority === 'urgent' ? 'text-destructive' : 'text-muted-foreground'} />
                  <span className={cn('size-1.5 shrink-0 rounded-full', status.dot)} title={t(status.labelKey)} />
                  <StaffAvatar email={c.assignee} className="size-4 text-[9px]" />
                </span>
              </button>
            );
          })
        ) : (
          <p className="px-3 py-10 text-center text-sm text-muted-foreground">{t('SUPPORT.STAFF.EMPTY')}</p>
        )}
      </div>
    </div>
  );
}
